// MeuFrete › BID de Frete (épico PO1-2413, H6/H7; revisão 5: a transportadora
// responde SÓ por aqui, não há página pública). Lista os BIDs em que ela é
// participante; o detalhe abre em modal por ?abrir=<id> (link do e-mail do
// BID): embarcador, áreas, prazo, download do pacote (URL assinada em nova
// aba), envio e reenvio da planilha modelo até o prazo, com os erros de
// layout devolvidos, e recusar. Não mostra preço de ninguém.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Modal, ModalBody, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard, { TableSurface } from "components/ShellCard";
import QuemEstaContratando from "components/gobid/QuemEstaContratando"; // PO1-3531 (épico PO1-3527)
import { useToast } from "hooks/toast";
import { enviarPropostaBidFrete, listarBidsFrete, mensagemErro, obterBidFrete, recusarBidFrete, urlPacoteBidFrete } from "services/meuFreteApi";
import { ufsDoBid } from "../../Empresa/GoBID/Bids/regras";
import { PARTICIPANTE_UI, arquivoValido, diasParaPrazo, errosPlanilha, fmtDataISO, fmtSiglas, matchesSearch, nomeEmbarcador, podeEnviar, podeRecusar } from "./regras";
import "../../Empresa/GoBID/Leiloes/styles.css";
import "../../Empresa/GoBID/Bids/styles.css";

function Badge({ tone, children }) {
  return <span className={`lf-badge lf-badge-${tone || "neutral"}`}>{children}</span>;
}

function estadoUi(estado) {
  return PARTICIPANTE_UI[estado] || { label: estado || "—", tone: "neutral" };
}

function textoPrazo(prazo) {
  const d = diasParaPrazo(prazo);
  if (d === null) return "—";
  if (d < 0) return `${fmtDataISO(prazo)} (encerrado)`;
  if (d === 0) return `${fmtDataISO(prazo)} (hoje)`;
  return `${fmtDataISO(prazo)} (${d} dia${d > 1 ? "s" : ""})`;
}

const ARQUIVOS = [
  { chave: "embarques", titulo: "Perfil dos embarques", descricao: "Uma linha por embarque: dia, origem e destino com CEP, peso, cubagem, volumes, valor da mercadoria, modal e serviço." },
  { chave: "modelo", titulo: "Planilha modelo", descricao: "Preencha as abas TB_FrTbCusto e TB_FrTbTaxa e envie aqui. Só esta planilha é aceita como proposta." },
];

export function BidFreteDetalhe({ isOpen, onClose, bidId, onChanged }) {
  const { addToast } = useToast();
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [errosLayout, setErrosLayout] = useState([]);
  const [erroEnvio, setErroEnvio] = useState(null);
  const [abrindo, setAbrindo] = useState(null);
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async () => {
    if (!bidId) return;
    setLoading(true);
    setErro(null);
    try {
      setBid(await obterBidFrete(bidId));
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível abrir o BID."));
    } finally {
      setLoading(false);
    }
  }, [bidId]);

  useEffect(() => {
    if (!isOpen) return;
    setBid(null);
    setArquivo(null);
    setErrosLayout([]);
    setErroEnvio(null);
    setRecusando(false);
    setMotivo("");
    carregar();
  }, [isOpen, carregar]);

  const abrirArquivo = async (chave) => {
    if (abrindo) return;
    setAbrindo(chave);
    try {
      const r = await urlPacoteBidFrete(bidId, chave);
      if (r && r.url) window.open(r.url, "_blank", "noopener");
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Pacote", description: mensagemErro(err, "Não foi possível baixar o arquivo."), icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setAbrindo(null);
    }
  };

  const enviar = async () => {
    const invalido = arquivoValido(arquivo);
    setErrosLayout([]);
    if (invalido) {
      setErroEnvio(invalido);
      return;
    }
    setEnviando(true);
    setErroEnvio(null);
    try {
      await enviarPropostaBidFrete(bidId, arquivo);
      addToast({ place: "tr", type: "success", title: "Proposta enviada", description: "O embarcador recebe a sua planilha. Você pode reenviar até o prazo.", icon: "tim-icons icon-check-2" });
      setArquivo(null);
      setInputKey((k) => k + 1);
      await carregar();
      if (onChanged) onChanged();
    } catch (err) {
      setErrosLayout(errosPlanilha(err));
      setErroEnvio(mensagemErro(err, "A planilha não foi aceita."));
    } finally {
      setEnviando(false);
    }
  };

  const recusar = async () => {
    setBusy(true);
    try {
      await recusarBidFrete(bidId, { motivo: motivo.trim() || undefined });
      setRecusando(false);
      await carregar();
      if (onChanged) onChanged();
    } catch (err) {
      setErroEnvio(mensagemErro(err, "Não foi possível recusar."));
    } finally {
      setBusy(false);
    }
  };

  const ui = bid ? estadoUi(bid.estado) : null;
  const areas = bid ? fmtSiglas(bid.areas && bid.areas.length ? bid.areas : ufsDoBid(bid)) : "—";
  const enviavel = podeEnviar(bid);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" modalClassName="modal-align-top" contentClassName="lf-modal" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>{bid ? `BID de Frete · ${bid.nome}` : "BID de Frete"}</span>
          {bid ? <Badge tone={ui.tone}>{ui.label}</Badge> : null}
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>
      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading && !bid ? (
          <div className="lf-empty-state">
            <Spinner size="sm" /> Carregando...
          </div>
        ) : erro && !bid ? (
          <div className="lf-aviso lf-aviso-danger">{erro}</div>
        ) : bid ? (
          <>
            <div className="lf-kv" style={{ marginBottom: 12 }}>
              <div>
                <div className="lf-kv-label">Embarcador</div>
                <div className="lf-kv-value">{nomeEmbarcador(bid)}</div>
              </div>
              <div>
                <div className="lf-kv-label">Áreas (UFs de destino)</div>
                <div className="lf-kv-value">{areas}</div>
              </div>
              <div>
                <div className="lf-kv-label">Prazo das propostas</div>
                <div className="lf-kv-value">{textoPrazo(bid.prazoPropostas)}</div>
              </div>
              <div>
                <div className="lf-kv-label">Sua proposta</div>
                <div className="lf-kv-value">{bid.propostaEm ? `enviada em ${fmtDataISO(bid.propostaEm)}` : "não enviada"}</div>
              </div>
            </div>

            {/* PO1-3531: o backend só manda o perfil para participante de fora da carteira. */}
            <QuemEstaContratando perfil={bid.perfilEmbarcador} />

            <div className="lf-section">
              <div className="lf-section-title">Pacote do BID</div>
              <div className="bf-pacote-arquivos">
                {ARQUIVOS.map((a) => (
                  <div key={a.chave} className="bf-pacote-arquivo">
                    <div className="lf-kv-value">{a.titulo}</div>
                    <div className="lf-muted">{a.descricao}</div>
                    <div>
                      <Button color="info" size="sm" outline onClick={() => abrirArquivo(a.chave)} disabled={Boolean(abrindo)}>
                        {abrindo === a.chave ? <Spinner size="sm" /> : "Baixar"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="lf-muted" style={{ marginTop: 6 }}>
                O link de download vale 15 minutos; clique de novo para gerar outro.
              </div>
            </div>

            {enviavel ? (
              <div className="lf-section">
                <div className="lf-section-title">{bid.propostaEm ? "Reenviar a planilha modelo" : "Enviar a planilha modelo"}</div>
                <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap" }}>
                  <Input
                    key={inputKey}
                    type="file"
                    accept=".xlsx,.xlsm"
                    aria-label="Planilha modelo"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      setArquivo(f || null);
                      setErroEnvio(null);
                      setErrosLayout([]);
                    }}
                  />
                  <Button color="primary" size="sm" onClick={enviar} disabled={enviando}>
                    {enviando ? <Spinner size="sm" /> : bid.propostaEm ? "Reenviar" : "Enviar proposta"}
                  </Button>
                </div>
                {bid.propostaEm ? <div className="lf-muted">O reenvio substitui a proposta anterior.</div> : null}
                {erroEnvio ? <div className="lf-aviso lf-aviso-danger" style={{ marginTop: 8 }}>{erroEnvio}</div> : null}
                {errosLayout.length ? (
                  <div className="bf-erros-planilha" data-testid="bf-erros-planilha">
                    <ul style={{ paddingLeft: 18 }}>
                      {errosLayout.map((e, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={i}>{e.texto}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="lf-aviso lf-aviso-info">
                {bid.estado === "RECUSOU" ? "Você recusou este BID." : "O prazo das propostas acabou; o envio está fechado."}
              </div>
            )}

            {podeRecusar(bid) ? (
              recusando ? (
                <div className="lf-aviso lf-aviso-warning">
                  <div style={{ marginBottom: 6 }}>Recusar avisa o embarcador que você não vai participar. Motivo (opcional):</div>
                  <Input
                    type="textarea"
                    rows={2}
                    maxLength={500}
                    value={motivo}
                    aria-label="Motivo da recusa"
                    onChange={(e) => {
                      const v = e.target.value;
                      setMotivo(v);
                    }}
                  />
                  <div style={{ marginTop: 6 }}>
                    <Button color="danger" size="sm" onClick={recusar} disabled={busy}>
                      {busy ? <Spinner size="sm" /> : "Confirmar recusa"}
                    </Button>{" "}
                    <Button color="secondary" size="sm" outline onClick={() => setRecusando(false)}>
                      Desistir
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <Button color="danger" outline size="sm" onClick={() => setRecusando(true)}>
                    Não vou participar
                  </Button>
                </div>
              )
            ) : null}
          </>
        ) : null}
      </ModalBody>
    </Modal>
  );
}

export default function BidFreteTransportadora() {
  const history = useHistory();
  const { addToast } = useToast();
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState("");
  const [detalheId, setDetalheId] = useState(null);
  const abrirRef = useRef(new URLSearchParams(history.location.search).get("abrir"));

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await listarBidsFrete();
      setItens(Array.isArray(r) ? r : (r && r.items) || []);
    } catch (err) {
      const msg = mensagemErro(err, "Erro ao carregar os BIDs.");
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "BID de Frete", description: msg, icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const abrir = abrirRef.current;
    if (!abrir) return;
    abrirRef.current = null;
    setDetalheId(abrir);
    history.replace({ pathname: history.location.pathname, search: "" });
  }, [history]);

  const visiveis = itens.filter((i) => matchesSearch(i, busca));

  return (
    <div className="content ff-page">
      <PageHero
        eyebrow="MeuFrete"
        title="BID de Frete"
        description="Convites de BID dos embarcadores que você atende: baixe o pacote com o perfil dos embarques, preencha a planilha modelo e envie a proposta até o prazo."
        actions={<PageHeroRefreshButton onClick={carregar} loading={loading} />}
      />
      <FilterBand mode="client" searchLabel="Buscar" searchPlaceholder="Nome do BID ou embarcador..." searchValue={busca} onSearchChange={setBusca} showFiltersButton={false} />
      <ShellCard title="Convites" description={`${visiveis.length} BID(s)`}>
        <TableSurface>
          {erro ? (
            <div className="lf-empty-state">{erro}</div>
          ) : loading && !itens.length ? (
            <div className="lf-empty-state">
              <Spinner size="sm" /> Carregando...
            </div>
          ) : visiveis.length === 0 ? (
            <div className="lf-empty-state">Nenhum convite de BID por enquanto.</div>
          ) : (
            <Table className="ff-table bf-table hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  <th>BID</th>
                  <th>Embarcador</th>
                  <th>Áreas</th>
                  <th>Prazo</th>
                  <th>Situação</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((i) => {
                  const ui = estadoUi(i.estado);
                  return (
                    <tr key={i.id} className="is-clicavel" onClick={() => setDetalheId(i.id)}>
                      <td>
                        <strong>{i.nome}</strong>
                      </td>
                      <td>{nomeEmbarcador(i)}</td>
                      <td>{fmtSiglas(i.areas && i.areas.length ? i.areas : ufsDoBid(i))}</td>
                      <td>{textoPrazo(i.prazoPropostas)}</td>
                      <td>
                        <Badge tone={ui.tone}>{ui.label}</Badge>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="lf-btn-abrir"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetalheId(i.id);
                          }}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </TableSurface>
      </ShellCard>

      <BidFreteDetalhe
        isOpen={Boolean(detalheId)}
        bidId={detalheId}
        onClose={() => {
          setDetalheId(null);
          carregar();
        }}
        onChanged={carregar}
      />
    </div>
  );
}
